import React, { useState } from 'react';
import { X, Lock, Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const ChangePasswordDialog = ({ isOpen, onClose }) => {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validation
    if (!oldPassword || !newPassword || !confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (oldPassword === newPassword) {
      setError('New password must be different from current password');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${BACKEND_URL}/api/user/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          old_password: oldPassword,
          new_password: newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
        
        // Close dialog after 2 seconds
        setTimeout(() => {
          onClose();
          setSuccess(false);
        }, 2000);
      } else {
        setError(data.detail || 'Failed to change password');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error changing password:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setError('');
      setSuccess(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  const Field = ({ label, value, onChange, show, setShow, placeholder }) => (
    <div>
      <label className="block font-tac-heading" style={{ fontSize: 12.5, fontWeight: 600, color: '#55555C', marginBottom: 7 }}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, border: '1px solid #E4E4E9', borderRadius: 9, background: '#FBFBFC', padding: '0 13px' }}>
        <input
          type={show ? 'text' : 'password'} value={value} onChange={onChange} disabled={loading || success} placeholder={placeholder}
          style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 14, color: '#141414', padding: '12px 0' }}
        />
        <button type="button" onClick={() => setShow((s) => !s)} disabled={loading || success} style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex' }}>
          {show ? <EyeOff className="w-4 h-4" style={{ color: '#9A9AA0' }} /> : <Eye className="w-4 h-4" style={{ color: '#9A9AA0' }} />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4 font-tac-body" style={{ background: 'rgba(20,20,20,0.4)' }}>
      <div className="w-full" style={{ maxWidth: 460, background: '#fff', borderRadius: 14, boxShadow: '0 18px 48px rgba(20,20,20,0.2)', padding: 28 }}>
        <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-[#BF212F]" />
            <h2 className="font-tac-display" style={{ fontWeight: 800, fontSize: 19, color: '#141414' }}>Change password</h2>
          </div>
          <button onClick={handleClose} disabled={loading} style={{ width: 30, height: 30, borderRadius: '50%', border: '1px solid #E4E4E9', background: '#fff', color: '#55555C', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X className="w-4 h-4" />
          </button>
        </div>
        <div style={{ fontSize: 13, color: '#55555C', marginBottom: 20 }}>Keep your account secure.</div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {success && (
            <div style={{ background: '#F0FDF4', border: '1px solid #22C55E', color: '#166534', padding: '12px 14px', borderRadius: 10, fontSize: 13 }}>Password changed successfully!</div>
          )}
          {error && (
            <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B', padding: '12px 14px', borderRadius: 10, fontSize: 13 }}>{error}</div>
          )}
          <Field label="Current password" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} show={showOldPassword} setShow={setShowOldPassword} placeholder="Enter current password" />
          <Field label="New password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} show={showNewPassword} setShow={setShowNewPassword} placeholder="At least 6 characters" />
          <Field label="Confirm new password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} show={showConfirmPassword} setShow={setShowConfirmPassword} placeholder="Re-enter new password" />

          <div style={{ background: '#FBFBFC', border: '1px solid #E4E4E9', borderRadius: 10, padding: 12 }}>
            <p className="font-tac-mono" style={{ fontSize: 10, color: '#9A9AA0', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>Requirements</p>
            <ul style={{ fontSize: 12.5, color: '#55555C', listStyle: 'disc', paddingLeft: 18, lineHeight: 1.7 }}>
              <li>At least 6 characters long</li>
              <li>Different from your current password</li>
            </ul>
          </div>

          <div className="flex gap-3" style={{ paddingTop: 2 }}>
            <button type="button" onClick={handleClose} disabled={loading} className="font-tac-heading flex-1" style={{ border: '1px solid #E4E4E9', color: '#55555C', borderRadius: 9, padding: '11px 16px', fontWeight: 600, fontSize: 13.5, cursor: 'pointer', background: '#fff' }}>Cancel</button>
            <button type="submit" disabled={loading || success} className="font-tac-heading flex-1 text-white" style={{ background: '#BF212F', borderRadius: 9, padding: '11px 16px', fontWeight: 600, fontSize: 13.5, cursor: 'pointer', opacity: loading || success ? 0.6 : 1 }}>{loading ? 'Changing…' : 'Change password'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordDialog;
